import _ from 'lodash';
import { CrossFrameUtils } from '@src/utils';
import {
    InvalidInputException,
    VoiceException,
    UnsupportedBrowserException,
    AudioException,
    InterruptedAudioException,
    HigherPriorityAudioPlayingException,
    VoiceDependencyFailureException,
    VoiceNotEnabledException,
    AudioRecordingException,
    RecordingAlreadyInProgressException,
    AudioProcessingException,
    SpeechRecognitionException,
    UnexpectedIntentException,
    RepeatIntentException,
    InvalidSpeechException,
    InvalidSpeechInputException,
    UnrecognizedSpeechException,
    StopIntentException,
    PauseIntentException,
    AlternativeBotException,
    WakeWordException,
    WakeWordValidUnknownActionException,
    WakeWordUnknownActionException,
    WakeWordInvalidActionException,
    WakeWordInitializationUnavailableException,
    WakeWordListeningUnavailableException,
    WakeWordDetectionFailureException,
    WakeWordUnknownErrorException,
    WakeWordListeningInterruptedException,
    WakeWordTimeoutException,
    ConfirmationDeclinedException,
} from '@services/voice/VoiceExceptions';

/**
 * Allows to map names to constructors. This makes it easy to translate from serialized object
 * (think javascript Object) to an actual Constructor. This is needed to be able to send
 * serialized objects over Cross Frame calls and keep the object type.
 */
export class VoiceCrossFrameWrapper {
    // objectName => objectConstructor
    private objectMap = {};

    /**
     * Registers all the Objects that may need to be translated.
     */
    constructor() {
        this.registerObject('VoiceException', VoiceException);
        this.registerObject('VoiceDependencyFailureException', VoiceDependencyFailureException);
        this.registerObject('InvalidInputException', InvalidInputException);
        this.registerObject('UnsupportedBrowserException', UnsupportedBrowserException);
        this.registerObject('AudioException', AudioException);
        this.registerObject('InterruptedAudioException', InterruptedAudioException);
        this.registerObject('HigherPriorityAudioPlayingException', HigherPriorityAudioPlayingException);
        this.registerObject('VoiceNotEnabledException', VoiceNotEnabledException);
        this.registerObject('AudioRecordingException', AudioRecordingException);
        this.registerObject('RecordingAlreadyInProgressException', RecordingAlreadyInProgressException);
        this.registerObject('AudioProcessingException', AudioProcessingException);
        this.registerObject('SpeechRecognitionException', SpeechRecognitionException);
        this.registerObject('UnexpectedIntentException', UnexpectedIntentException);
        this.registerObject('RepeatIntentException', RepeatIntentException);
        this.registerObject('InvalidSpeechException', InvalidSpeechException);
        this.registerObject('InvalidSpeechInputException', InvalidSpeechInputException);
        this.registerObject('UnrecognizedSpeechException', UnrecognizedSpeechException);
        this.registerObject('StopIntentException', StopIntentException);
        this.registerObject('PauseIntentException', PauseIntentException);
        this.registerObject('AlternativeBotException', AlternativeBotException);
        this.registerObject('WakeWordException', WakeWordException);
        this.registerObject('WakeWordValidUnknownActionException', WakeWordValidUnknownActionException);
        this.registerObject('WakeWordUnknownActionException', WakeWordUnknownActionException);
        this.registerObject('WakeWordInvalidActionException', WakeWordInvalidActionException);
        this.registerObject('WakeWordInitializationUnavailableException', WakeWordInitializationUnavailableException);
        this.registerObject('WakeWordListeningUnavailableException', WakeWordListeningUnavailableException);
        this.registerObject('WakeWordDetectionFailureException', WakeWordDetectionFailureException);
        this.registerObject('WakeWordUnknownErrorException', WakeWordUnknownErrorException);
        this.registerObject('WakeWordListeningInterruptedException', WakeWordListeningInterruptedException);
        this.registerObject('WakeWordTimeoutException', WakeWordTimeoutException);
        this.registerObject('ConfirmationDeclinedException', ConfirmationDeclinedException);
    }

    /**
     * Translates the passed object into its appropriate constructor (if possible). Returns the original
     * object if its constructor is not registered. Note that this method is safe to call with `undefined`
     * and `null` (it simply returns the same).
     *
     * @param {object} obj - The object to translate. This object must have the `model` property
     *  and that property must have been registered with the object's constructor for it to translate successfully.
     * @return {object} - An instance of the mapped constructor, if available, or the original object
     *  if it isn't.
     */
    public translate(obj) {
        if (!obj || !obj.model || !this.objectMap[obj.model]) {
            return obj;
        }
        const expObj = this.objectMap[obj.model];
        return new expObj().deserialize(obj);
    }

    /**
     * Registers a name to a Constructor so that an object can be translated later.
     * Note that this ensures that the name hasn't been registered before with a different constructor to
     * prevent unintended overrides.
     *
     * @param {string} name - the name of the constructor.
     * @param {Constructor} Constructor - the reference to the Constructor. This constructor must implement the
     *  static method `deserialize` which should accept the object and should return an instance.
     */
    public registerObject(name: string, Constructor) {
        if (!name) {
            throw new Error('name is required');
        }

        if (!Constructor) {
            throw new Error('Constructor is required');
        }

        const dummyObj = new Constructor({ message: 'dummyMessage' });
        if (!dummyObj.deserialize || !_.isFunction(dummyObj.deserialize)) {
            throw new Error('Constructor must have a deserialize method');
        }

        if (this.objectMap[name] && this.objectMap[name] !== Constructor) {
            console.error(`Different constructor for name ${name} was registered. Current: ${this.objectMap[name]} new: ${Constructor}`);
            throw new Error(`Exception for name ${name} was already registered`);
        }

        this.objectMap[name] = Constructor;
    }

    /**
     * Wrapper around CrossFrame that provides Object translation (mainly to preserve the Exception type).
     * Makes a cross frame call for a method with a specified action and parameters.
     * Then, converts the returned object to a proper
     * instance (if available) and resolves if the call was successful and rejects if it wasn't.
     *
     * @param {string} method - the listener to call
     * @param {string} action - the action to call within the listener.
     * @param {object} parameters - object containing the parameters to execute the action.
     * @return {Promise}
     */
    public async call(method: string, action: string, parameters: object) {
        try {
            const response = await CrossFrameUtils.sendMessageToOtherFrame({
                frame: window,
                message: {
                    method,
                    data: {
                        action,
                        parameters,
                    },
                },
            });
            console.debug(`Successful call to ${action} response`);
            return this.translate(response);
        } catch (error) {
            console.debug(`Failed call to ${action} error`);
            return Promise.reject(this.translate(error));
        }
    }
}
